/**
 * Per-user portal preferences - stored in localStorage per browser, NOT
 * on the server. Partner brand_config from /profile still drives the
 * default palette; these preferences layer on top for the logged-in user.
 *
 * Shape:
 *   - theme:   light | dark | system (follows OS prefers-color-scheme)
 *   - density: comfortable | compact (tightens vertical spacing)
 *   - accent:  undefined uses brand_config.primary_color; otherwise an
 *              HSL triple like "221 83% 53%"
 *   - reducedMotion: boolean; undefined → respect OS preference
 */

export type Theme = "light" | "dark" | "system";
export type Density = "comfortable" | "compact";

export interface Preferences {
  theme: Theme;
  density: Density;
  // `| undefined` makes the key valid to pass explicitly undefined when
  // clearing an override (exactOptionalPropertyTypes is strict about
  // pure-optional vs explicit-undefined).
  accent?: string | undefined; // HSL triple "H S% L%"
  reducedMotion?: boolean | undefined;
}

export const DEFAULT_PREFERENCES: Preferences = {
  theme: "system",
  density: "comfortable",
};

const KEY = "finterra.admin.prefs";

export function loadPreferences(): Preferences {
  if (typeof window === "undefined") return DEFAULT_PREFERENCES;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    const out: Preferences = {
      theme: parsed.theme ?? DEFAULT_PREFERENCES.theme,
      density: parsed.density ?? DEFAULT_PREFERENCES.density,
    };
    // exactOptionalPropertyTypes - only assign optional keys when defined.
    if (parsed.accent !== undefined) out.accent = parsed.accent;
    if (parsed.reducedMotion !== undefined) out.reducedMotion = parsed.reducedMotion;
    return out;
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

export function savePreferences(prefs: Preferences): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // storage full / private mode - ignore, session-only prefs still work
  }
}

/** Reads the OS setting; used when theme === 'system'. */
export function osPrefersDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

export function osPrefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

/**
 * Apply preferences to the DOM. Does three things:
 *   1. Toggle the `dark` class on <html> for Tailwind dark: variants.
 *   2. Set data-density + data-reduced-motion on <html> so CSS can key off.
 *   3. Write --primary + --ring CSS variables when accent is overridden.
 */
export function applyPreferences(prefs: Preferences): void {
  if (typeof document === "undefined") return;
  const html = document.documentElement;

  const dark = prefs.theme === "dark" || (prefs.theme === "system" && osPrefersDark());
  html.classList.toggle("dark", dark);

  html.dataset.density = prefs.density;

  const reduced = prefs.reducedMotion ?? osPrefersReducedMotion();
  html.dataset.reducedMotion = String(reduced);

  if (prefs.accent) {
    html.style.setProperty("--primary", prefs.accent);
    html.style.setProperty("--ring", prefs.accent);
  } else {
    html.style.removeProperty("--primary");
    html.style.removeProperty("--ring");
  }
}

/** Preset accent colors shown in the popover. HSL triples for CSS vars. */
export const ACCENT_PRESETS: Array<{ name: string; value: string }> = [
  { name: "Default", value: "" }, // "" → use brand_config primary
  { name: "Indigo", value: "243 75% 59%" },
  { name: "Cobalt", value: "221 83% 53%" },
  { name: "Teal", value: "173 80% 40%" },
  { name: "Emerald", value: "158 64% 42%" },
  { name: "Amber", value: "38 92% 50%" },
  { name: "Rose", value: "346 77% 50%" },
  { name: "Violet", value: "262 83% 58%" },
];
